import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Plus, Globe } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { HelpBanner } from '@/components/ui/help-banner'
import type { PaginatedResponse, TenantResponse, BookmakerResponse } from '@/api/types'

export function TenantsPage() {
  const { t } = useTranslation(['tenants', 'common'])
  const navigate = useNavigate()
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const queryClient = useQueryClient()
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['tenants', offset],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>(`/tenants?offset=${offset}&limit=${limit}`),
  })

  const createMutation = useMutation({
    mutationFn: (name: string) => api.post('/tenants', { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      setDialogOpen(false)
      setNewName('')
    },
  })

  // Fetch all bookmakers to show bookmakers per tenant
  const { data: bookmakersData } = useQuery({
    queryKey: ['bookmakers-all'],
    queryFn: () => api.get<PaginatedResponse<BookmakerResponse>>('/bookmakers?offset=0&limit=500'),
  })

  const bookmakersByTenant = useMemo(() => {
    const map: Record<number, BookmakerResponse[]> = {}
    for (const s of bookmakersData?.data.data ?? []) {
      if (!map[s.tenant_id]) map[s.tenant_id] = []
      map[s.tenant_id].push(s)
    }
    return map
  }, [bookmakersData])

  const tenants = data?.data.data ?? []
  const total = data?.data.total ?? 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold">{t('nav.tenants', { ns: 'common' })}</h2>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4 mr-2" />{t('newTenant')}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('createTenant')}</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(newName) }} className="flex flex-col gap-4">
              <div>
                <Label htmlFor="tenant-name">{t('labels.name', { ns: 'common' })}</Label>
                <Input id="tenant-name" value={newName} onChange={(e) => setNewName(e.target.value)} required />
              </div>
              <Button type="submit" disabled={createMutation.isPending}>{t('buttons.create', { ns: 'common' })}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <HelpBanner storageKey="tenants">
        <p>{t('help.line1')}</p>
        <p>{t('help.line2')}</p>
      </HelpBanner>

      {isLoading ? (
        <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p>
      ) : (
        <>
          <div className="border rounded-md">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium">{t('labels.name', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('entities.bookmakers', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.status', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.created', { ns: 'common' })}</th>
              </tr></thead>
              <tbody>
                {tenants.map((tn) => {
                  const bookmakers = bookmakersByTenant[tn.id] ?? []
                  return (
                    <tr key={tn.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/tenants/${tn.id}`)}>
                      <td className="p-3 font-medium">{tn.name}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="shrink-0">
                            <Globe className="h-3 w-3 mr-1" />{bookmakers.length}
                          </Badge>
                          {bookmakers.length > 0 && (
                            <span className="text-xs text-muted-foreground truncate">
                              {bookmakers.map((s) => s.name).join(', ')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3"><Badge variant={tn.status === 'active' ? 'default' : 'secondary'}>{tn.status}</Badge></td>
                      <td className="p-3 text-muted-foreground">{new Date(tn.created_at).toLocaleDateString()}</td>
                    </tr>
                  )
                })}
                {tenants.length === 0 && <tr><td colSpan={4} className="p-3 text-center text-muted-foreground">{t('empty')}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">{t('labels.total', { ns: 'common' })}: {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>{t('pagination.previous', { ns: 'common' })}</Button>
              <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>{t('pagination.next', { ns: 'common' })}</Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
